import React from 'react';
import './MicrophoneLevelMeter.css';

type MeterStatus = 'requesting' | 'live' | 'suspended' | 'error' | 'unsupported' | 'stopped';
type LevelBand = 'quiet' | 'good' | 'loud' | 'clipping';

type Props = {
    deviceId: string;
    enabled: boolean;
    onMicrophoneReady: () => Promise<void>;
};

export type MicrophoneLevelMeterHandle = {stop: () => void};

const MIN_DB = -60;
const SAMPLE_INTERVAL_MS = 50;
const LEVEL_LABELS: Record<LevelBand, string> = {
    quiet: 'Quiet — speak or move closer',
    good: 'Good level',
    loud: 'Loud — move a little farther away',
    clipping: 'Too loud — audio may distort',
};

// Measure digital peak level, not room loudness; silence stays at the meter floor.
export function getMicrophonePeakDb(samples: Float32Array): number {
    let peak = 0;
    for (const sample of samples) {
        peak = Math.max(peak, Math.abs(sample));
    }
    return peak > 0 ? Math.max(MIN_DB, Math.min(0, 20 * Math.log10(peak))) : MIN_DB;
}

// Leave headroom for louder speech and flag peaks near digital clipping.
function getLevelBand(peakDb: number): LevelBand {
    if (peakDb >= -1) return 'clipping';
    if (peakDb >= -6) return 'loud';
    return peakDb >= -30 ? 'good' : 'quiet';
}

// Translate browser capture failures into a useful microphone check message.
function getMicrophoneError(error: unknown): string {
    const name = error && typeof error === 'object' && 'name' in error ? String(error.name) : '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return 'Microphone access is blocked. Allow it in your browser to check the level.';
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return 'This microphone is unavailable. Choose another input.';
    }
    if (name === 'NotReadableError') {
        return 'The microphone could not be opened. Check its connection or whether another app is using it.';
    }
    return 'The microphone level check could not start. Try again or choose another input.';
}

// Preview only the selected microphone; audio is neither recorded nor played through speakers.
export const MicrophoneLevelMeter = React.forwardRef<MicrophoneLevelMeterHandle, Props>(
    function MicrophoneLevelMeter({deviceId, enabled, onMicrophoneReady}, forwardedRef) {
        const [status, setStatus] = React.useState<MeterStatus>('requesting');
        const [peakDb, setPeakDb] = React.useState(MIN_DB);
        const [errorMessage, setErrorMessage] = React.useState('');
        const [attempt, setAttempt] = React.useState(0);
        const stopRef = React.useRef(() => {});
        const resumeRef = React.useRef(() => {});
        const onReadyRef = React.useRef(onMicrophoneReady);
        const labelId = React.useId();

        // Directory refreshes must not restart the meter when the parent rerenders.
        React.useEffect(() => {
            onReadyRef.current = onMicrophoneReady;
        }, [onMicrophoneReady]);

        React.useImperativeHandle(forwardedRef, () => ({stop: () => stopRef.current()}), []);

        React.useEffect(() => {
            if (!enabled) {
                setStatus('stopped');
                return;
            }

            const AudioContextConstructor = window.AudioContext ||
                (window as Window & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
            if (!navigator.mediaDevices?.getUserMedia || !AudioContextConstructor) {
                setStatus('unsupported');
                return;
            }

            let cancelled = false;
            let stream: MediaStream | null = null;
            let context: AudioContext | null = null;
            let source: MediaStreamAudioSourceNode | null = null;
            let analyser: AnalyserNode | null = null;
            let timer: ReturnType<typeof setInterval> | undefined;
            let displayedPeakDb = MIN_DB;
            let clippingUntil = 0;
            setStatus('requesting');
            setPeakDb(MIN_DB);
            setErrorMessage('');

            // Cancel pending acquisition and release owned tracks synchronously before recording starts.
            const stop = () => {
                cancelled = true;
                if (timer !== undefined) clearInterval(timer);
                stream?.getTracks().forEach((track) => {
                    track.removeEventListener('ended', handleTrackEnded);
                    track.stop();
                });
                source?.disconnect();
                analyser?.disconnect();
                if (context) {
                    context.onstatechange = null;
                    if (context.state !== 'closed') void context.close().catch(() => {});
                }
                stream = null;
                source = null;
                analyser = null;
                context = null;
            };

            // A disconnected input must not leave a frozen level looking like live audio.
            const handleTrackEnded = () => {
                if (cancelled) return;
                setErrorMessage('The microphone was disconnected. Reconnect it or choose another input.');
                setStatus('error');
                setPeakDb(MIN_DB);
                stop();
            };
            stopRef.current = stop;

            // Some browsers require a user gesture to resume a suspended audio context.
            resumeRef.current = () => {
                if (!cancelled && context && context.state !== 'closed') {
                    void context.resume().catch(() => {
                        if (!cancelled) setStatus('suspended');
                    });
                }
            };

            // Match the recorder's device constraints, including browser audio-processing defaults.
            const start = async () => {
                try {
                    const acquiredStream = await navigator.mediaDevices.getUserMedia({
                        audio: deviceId ? {deviceId: {exact: deviceId}} : true,
                    });
                    if (cancelled) {
                        acquiredStream.getTracks().forEach((track) => track.stop());
                        return;
                    }

                    stream = acquiredStream;
                    stream.getTracks().forEach((track) => track.addEventListener('ended', handleTrackEnded));
                    context = new AudioContextConstructor();
                    source = context.createMediaStreamSource(stream);
                    analyser = context.createAnalyser();
                    analyser.fftSize = 2048;
                    source.connect(analyser);
                    const samples = new Float32Array(analyser.fftSize);
                    context.onstatechange = () => {
                        if (!cancelled) setStatus(context?.state === 'running' ? 'live' : 'suspended');
                    };
                    setStatus(context.state === 'running' ? 'live' : 'suspended');
                    if (context.state !== 'running') resumeRef.current();
                    void onReadyRef.current().catch(() => {});

                    // Fast attack, slower decay and a short clipping hold make speech peaks readable.
                    timer = setInterval(() => {
                        if (cancelled || !analyser || context?.state !== 'running') return;
                        analyser.getFloatTimeDomainData(samples);
                        const instantPeakDb = getMicrophonePeakDb(samples);
                        if (instantPeakDb >= -1) clippingUntil = Date.now() + 1000;
                        displayedPeakDb = Math.max(instantPeakDb, displayedPeakDb - 1.2);
                        setPeakDb(Date.now() < clippingUntil ? 0 : displayedPeakDb);
                    }, SAMPLE_INTERVAL_MS);
                } catch (error) {
                    if (!cancelled) {
                        setErrorMessage(getMicrophoneError(error));
                        setStatus('error');
                        stop();
                    }
                }
            };
            void start();
            return stop;
        }, [deviceId, enabled, attempt]);

        const isLive = status === 'live';
        const band = getLevelBand(peakDb);
        const levelLabel = isLive ? LEVEL_LABELS[band] : {
            requesting: 'Allow microphone access to check the level.',
            suspended: 'The microphone level check is paused.',
            error: errorMessage,
            unsupported: 'Live microphone levels are not supported in this browser.',
            stopped: 'Microphone check paused.',
        }[status as Exclude<MeterStatus, 'live'>];

        return (
            <div className='microphone-level-check'>
                <div className='microphone-level-heading'>
                    <span id={labelId}>Input level</span>
                    <span className={`microphone-level-status ${isLive ? band : ''}`}>{levelLabel}</span>
                </div>
                <div
                    className='microphone-level-track'
                    role='meter'
                    aria-labelledby={labelId}
                    aria-valuemin={MIN_DB}
                    aria-valuemax={0}
                    aria-valuenow={isLive ? Math.round(peakDb) : MIN_DB}
                    aria-valuetext={isLive ? `${Math.round(peakDb)} dBFS. ${levelLabel}` : levelLabel}
                >
                    <span
                        className={`microphone-level-fill ${band}`}
                        style={{width: `${isLive ? ((peakDb - MIN_DB) / -MIN_DB) * 100 : 0}%`}}
                    />
                </div>
                <div className='microphone-level-scale' aria-hidden='true'>
                    <span>Quiet</span><span>Good</span><span>Too loud</span>
                </div>
                {status === 'suspended' ? (
                    <button type='button' className='microphone-level-retry' onClick={() => resumeRef.current()}>
                        Enable level check
                    </button>
                ) : status === 'error' ? (
                    <button type='button' className='microphone-level-retry' onClick={() => setAttempt((value) => value + 1)}>
                        Retry microphone check
                    </button>
                ) : (
                    <p className='microphone-level-help'>Speak normally and aim for green. This is a live check; recording starts when you press Record.</p>
                )}
            </div>
        );
    }
);
